import React from 'react';

import { useBreakpoint } from '$hooks/events';
import { Image } from '$partials/media';
import { Carousel, CarouselPagination, CarouselSlide, CarouselWrapper } from '$partials/base/carousel';

import { formatPrice, formatRentFrequency } from '$utilities/format';
import { Tag } from '$partials/ui';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').PartnershipFeaturedPropertiesImageCarousel}
 */
const PartnershipFeaturedPropertiesImageCarousel = ({
    __typename,
    publicSellingStatus,
    images,
    height,
}) => {
    const breakpoint = useBreakpoint([ 'main', 'sm', 'md' ], 'main');

    const isRental = __typename === 'PartnershipRentalPropertyPage';
    const isBuy = __typename === 'PartnershipPropertyPage';

    return <Carousel swiperConfig={{
        slidesPerView: 1,
        spaceBetween: 0,
        breakpoints: {
            640: {
                slidesPerView: 1,
                spaceBetween: 0,
            },
            1024: {
                slidesPerView: 1,
                spaceBetween: 1
            }
        },
    }}>
        <div className='overflow-hidden featured-properties-image'>
            <CarouselWrapper
                className='swiper-wrapper'>
                {isRental && (
                    <div className='absolute z1'>
                        <Tag
                            isPartnership={true}
                            title={publicSellingStatus ? publicSellingStatus : 'For Rent'}/>
                    </div>
                )}
                {isBuy && (
                    <div className='absolute z1'>
                        <Tag
                            isPartnership={true}
                            title={publicSellingStatus ? publicSellingStatus : 'For Sale'}/>
                    </div>
                )}
                {images?.map((item, i) => {
                    return (
                        <CarouselSlide
                            className='flex carousel-slide'
                            index={i}
                            key={`featured-properties-image-${item?.image?.id}`}>
                            <div
                                style={{fontSize: height}}>
                                <div
                                    className='flex width-100'
                                    style={{
                                        height: breakpoint === 'md' ? height : 'auto',
                                        width:  breakpoint === 'md' ? `${(item.image.width / item.image.height)}em` : '280px',
                                        maxWidth: '100%'
                                    }}>
                                    <Image {...item.image}/>
                                </div>
                            </div>
                        </CarouselSlide>
                    );
                })}
            </CarouselWrapper>
            {images.length > 1 && <div
                className='absolute z3 featured-properties-pagination transition-opacity'
                style={{marginTop: '-25px'}}>
                <CarouselPagination className='ml5 flex'/>
            </div>  }
        </div>

    </Carousel>;
};


/**
 * @type {import('$types/components/partials/modules.d.ts').PartnershipFeaturedProperties}
 */
const PartnershipFeaturedProperties = ({
    title,
    partnershipItems,
}) => {

    const properties = partnershipItems?.filter(item => item.page);
    let pattern = [];

    properties.forEach((_, index) => {
        if (index === 0) {
            pattern.push([0, 0]);
        } else if (pattern[pattern.length - 1].length === 1) {
            pattern[pattern.length - 1].push(index);
        } else {
            pattern.push([index]);
        }
    });

    return (
        <div>
            <div className='col-12 flex flex-wrap border-bottom grey'>
                <div className='col-12 pr7 py6 md-pr0 bg-white flex items-center pl5 sm-pl6'>
                    <h2 className='type-style-2 black'>{title}</h2>
                </div>
            </div>
            <div className='col-12 flex flex-wrap'>
                {properties?.length > 0 && properties.map((item, idx) => {
                    const page = item.page;
                    const colSize = pattern.findIndex(pair => pair.includes(idx)) % 2 === 0 ? 'small' : 'large';
                    const commonClass = 'text-decoration-reset px5 md-px6 pb5 pt5 bg-white grey border-top border-bottom flex md-justify-between flex-col';
                    const price = page.__typename === 'PartnershipPropertyPage' ? formatPrice(page.priceUsd, {currency: 'USD'}) : `${formatPrice(page.rentUsd, {currency: 'USD'})} ${formatRentFrequency(page.partnershipRentFrequency)}`;

                    if (colSize === 'small') {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8' } col-12 sm-col-6 md-col-5`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <PartnershipFeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-10 md-col-8 pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`}>
                                    <div>
                                        <p className='type-style-5 pb1 md-pb2'>
                                            {price}
                                        </p>
                                    </div>
                                    <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                    <div className='mt5'>
                                        <p className='type-style-5'>{page?.propertyName}</p>
                                        <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                    </div>
                                </div>
                            </a>
                        );
                    } else {
                        return (
                            <a
                                className={`${commonClass}${idx % 2 === 0 ? ' sm-border-right md-flex-col-reverse md-pt8 md-pb6' : ' sm-border-left md-pt6 md-pb8'} col-12 sm-col-6 md-col-7`}
                                href={page?.url}
                                key={`featured-property-${page?.id}-${idx}`}>

                                <PartnershipFeaturedPropertiesImageCarousel
                                    height='360px'
                                    images={page.listingMedia}
                                    {...page}/>

                                <div className={`black col-12 flex flex-wrap pt5${idx % 2 === 0 ? ' md-pt0 md-mb8' : ' md-pt8' }`} >
                                    <div className='col-12 md-col-4'>
                                        <p className='type-style-5 pb1 md-pb2'>
                                            {price}
                                        </p>
                                    </div>
                                    <div className='md-pl8 col-10 md-col-6'>
                                        <h3 className='type-style-3 mb4'>{page?.heading}</h3>
                                        <div className='mt5'>
                                            <p className='type-style-5'>{page?.propertyName}</p>
                                            <p className='type-style-5'>{page?.area} {page?.outwardCode}</p>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        );
                    }
                })}
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PartnershipFeaturedProperties;
